import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { deadlineLabels, money, shortDate, stageLabels } from '../lib/format';
import { dropIndex, dropTop, moveItem, rowGap, rowShift, type RowBox } from '../lib/queue-order';
import { haptic } from '../lib/telegram';
import type { ListResponse, Order, Schedule } from '../types';
import { Card, EmptyState, ErrorNotice, LoadingScreen, PageHeader } from '../components/ui';

/** Меньше этого сдвига — палец дрогнул, а не потащил заказ. */
const DRAG_THRESHOLD_PX = 4;

interface Drag {
  from: number;
  to: number;
  offset: number;
  rows: RowBox[];
  step: number;
}

export function QueuePage() {
  const queryClient = useQueryClient();
  const list = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  /*
   * Тот же жест, но доступный обработчику отпускания. Читать состояние внутри
   * апдейтера нельзя: там нельзя дёргать мутацию — в dev React вызывает
   * апдейтер дважды, и запрос ушёл бы на сервер два раза.
   */
  const current = useRef<Drag | null>(null);

  const setDragState = (next: Drag | null) => {
    current.current = next;
    setDrag(next);
  };

  const query = useQuery({
    queryKey: ['queue'],
    queryFn: () => api<ListResponse<Order>>('/queue'),
  });
  // Прогноз считает сервер: он один знает про рабочие дни, отпуска и оценки.
  const schedule = useQuery({
    queryKey: ['schedule'],
    queryFn: () => api<Schedule>('/artist/schedule'),
  });

  const reorder = useMutation({
    mutationFn: ({ orderIds }: { orderIds: string[]; previous: ListResponse<Order> }) =>
      api<ListResponse<Order>>('/queue/reorder', { method: 'POST', body: { orderIds } }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['queue'], updated);
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      // Порядок сдвинул все плановые даты — прогноз пересчитываем заново.
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: (_error, variables) => {
      // Сервер отказал — возвращаем список к тому, что он видел до жеста.
      queryClient.setQueryData(['queue'], variables.previous);
    },
  });

  const items = query.data?.items ?? [];

  const apply = (from: number, to: number) => {
    if (!query.data || from === to) return;

    const next = moveItem(items, from, to);
    queryClient.setQueryData(['queue'], { ...query.data, items: next });
    reorder.mutate({ orderIds: next.map((order) => order.id), previous: query.data });
  };

  const start = (index: number, event: React.PointerEvent<HTMLElement>) => {
    const container = list.current;
    if (!container || reorder.isPending) return;

    /*
     * Размеры строк снимаются один раз, в начале жеста. Дальше список не
     * перестраивается — соседи разъезжаются трансформацией, — поэтому
     * измеренное остаётся верным до самого конца перетаскивания.
     */
    const rows: RowBox[] = [...container.children].map((child) => {
      const element = child as HTMLElement;
      return { top: element.offsetTop, height: element.offsetHeight };
    });

    event.currentTarget.setPointerCapture(event.pointerId);
    // Просвет открывается ровно на перетаскиваемую строку, а не на первую в
    // списке: у строки с пометкой о просрочке высота своя.
    const step = rows[index]!.height + rowGap(rows);
    setDragState({ from: index, to: index, offset: 0, rows, step });
  };

  const move = (event: React.PointerEvent<HTMLElement>, startY: number) => {
    const active = current.current;
    if (!active) return;

    const offset = event.clientY - startY;
    if (Math.abs(offset) < DRAG_THRESHOLD_PX && active.offset === 0) return;

    setDragState({ ...active, offset, to: dropIndex(active.rows, active.from, offset) });
  };

  const finish = () => {
    const active = current.current;
    setDragState(null);
    if (active) apply(active.from, active.to);
  };

  return (
    <div className="schedule-page">
      <PageHeader eyebrow="Планирование" title="Расписание" />
      <nav className="schedule-tabs" aria-label="Режим расписания">
        <Link href="/queue" className="is-active" aria-current="page">
          Очередь
        </Link>
        <Link href="/calendar">Календарь</Link>
      </nav>

      <div className="queue-helper">
        <p>Тащите за ручку — порядок сохранится сразу</p>
        <span>{items.length} в очереди</span>
      </div>

      {query.isPending && <LoadingScreen label="Выстраиваем очередь" />}
      {(query.error || reorder.error) && <ErrorNotice error={query.error ?? reorder.error} />}
      {query.data?.items.length === 0 && (
        <EmptyState
          title="Очередь пуста"
          text="Принятые заказы встанут сюда сами — порядок вы поменяете как удобно."
        />
      )}

      <div className={`queue-list ${drag ? 'is-dragging' : ''}`} ref={list}>
        {items.map((order, index) => (
          <QueueRow
            key={order.id}
            order={order}
            index={index}
            total={items.length}
            drag={drag}
            disabled={reorder.isPending}
            onStart={start}
            onMove={move}
            onFinish={finish}
            onKeyboardMove={(direction) => {
              const target = index + direction;
              if (target >= 0 && target < items.length) apply(index, target);
            }}
          />
        ))}

        {/* Просвет открылся трансформацией соседей — сюда и встанет строка. */}
        {drag && drag.to !== drag.from && (
          <div
            className="queue-drop"
            aria-hidden="true"
            style={{
              top: dropTop(drag.rows, drag.from, drag.to),
              height: drag.rows[drag.from]!.height,
            }}
          >
            Сюда · место {drag.to + 1}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <Card className="card-row">
          <span className="meta">Ближайшее свободное окно</span>
          <strong>
            {schedule.data?.nextOpening ? shortDate(schedule.data.nextOpening) : 'сейчас'}
          </strong>
        </Card>
      )}
    </div>
  );
}

function QueueRow({
  order,
  index,
  total,
  drag,
  disabled,
  onStart,
  onMove,
  onFinish,
  onKeyboardMove,
}: {
  order: Order;
  index: number;
  total: number;
  drag: Drag | null;
  disabled: boolean;
  onStart: (index: number, event: React.PointerEvent<HTMLElement>) => void;
  onMove: (event: React.PointerEvent<HTMLElement>, startY: number) => void;
  onFinish: () => void;
  onKeyboardMove: (direction: -1 | 1) => void;
}) {
  const startY = useRef(0);
  const dragged = drag?.from === index;
  const shift = drag ? rowShift(index, drag.from, drag.to, drag.step) : 0;
  const offset = dragged ? drag!.offset : shift;

  return (
    <article
      className={`queue-item ${dragged ? 'is-dragged' : ''}`}
      style={offset === 0 ? undefined : { transform: `translateY(${offset}px)` }}
    >
      <button
        type="button"
        className="queue-item__handle"
        aria-label={`Переставить заказ ${order.publicNumber}, сейчас ${index + 1} из ${total}`}
        disabled={disabled}
        onPointerDown={(event) => {
          startY.current = event.clientY;
          onStart(index, event);
        }}
        onPointerMove={(event) => onMove(event, startY.current)}
        onPointerUp={onFinish}
        onPointerCancel={onFinish}
        // Мышью и пальцем тащат, с клавиатуры переставляют стрелками: жест не
        // должен быть единственным способом поменять порядок.
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            onKeyboardMove(event.key === 'ArrowUp' ? -1 : 1);
          }
        }}
      >
        <span aria-hidden="true">≡</span>
      </button>

      <div className="queue-item__position">{index + 1}</div>
      <span className="queue-item__avatar" aria-hidden="true">
        {order.client.displayName.slice(0, 1).toUpperCase()}
      </span>
      <div className="queue-item__main">
        <h2>
          {order.client.displayName} · {order.publicNumber}
        </h2>
        <p>
          {shortDate(order.deadline)} · {stageLabels[order.currentStage].toLowerCase()} ·{' '}
          {money(order.totalPrice, order.currency)}
        </p>
        {order.deadlineState === 'OVERDUE' && (
          <p className="meta text-danger">{deadlineLabels[order.deadlineState]}</p>
        )}
      </div>
    </article>
  );
}
